/**
 * Five9 WebSocket Keepalive Worker Service
 * 
 * Manages Web Worker for preventing browser tab suspension and maintaining
 * WebSocket connections by delegating ping timing to a background worker.
 */

type KeepaliveWorkerMessage = 
	| { type: "WORKER_READY"; timestamp: number; message: string }
	| { type: "KEEPALIVE_STARTED"; timestamp: number; intervalMs: number }
	| { type: "KEEPALIVE_STOPPED"; timestamp: number }
	| { type: "KEEPALIVE_TICK"; timestamp: number; intervalMs: number; message: string }
	| { type: "INTERVAL_UPDATED"; timestamp: number; intervalMs: number }
	| { type: "PONG"; timestamp: number; workerActive: boolean }
	| { type: "WORKER_ERROR"; timestamp: number; error: any };

type KeepaliveWorkerCommand = 
	| { type: "START_KEEPALIVE"; data?: { intervalMs?: number } }
	| { type: "STOP_KEEPALIVE" }
	| { type: "UPDATE_INTERVAL"; data: { intervalMs: number } }
	| { type: "PING" };

export class KeepaliveWorkerService {
	private worker: Worker | null = null;
	private isActive: boolean = false;
	private intervalMs: number = 30000; // 30 seconds default
	private onKeepaliveTick?: () => void;
	private onWorkerError?: (error: any) => void;

	constructor() {
		this.initializeWorker();
	}

	private initializeWorker(): void {
		try {
			this.worker = new Worker("/five9-websocket-tester/websocket-keepalive-worker.js");
			this.worker.addEventListener("message", this.handleWorkerMessage.bind(this));
			this.worker.addEventListener("error", this.handleWorkerError.bind(this));
			console.log("🔄 [KEEPALIVE_SERVICE] Web Worker initialized");
		} catch (error) {
			console.error("❌ [KEEPALIVE_SERVICE] Failed to initialize Web Worker:", error);
			this.handleWorkerError(error);
		}
	}

	private handleWorkerMessage(event: MessageEvent<KeepaliveWorkerMessage>): void {
		const { type, timestamp } = event.data;
		console.log(`📨 [KEEPALIVE_SERVICE] Received message: ${type}`, event.data);

		switch (type) {
			case "WORKER_READY":
				console.log("✅ [KEEPALIVE_SERVICE] Web Worker is ready");
				break;
			case "KEEPALIVE_STARTED":
				this.isActive = true;
				this.intervalMs = event.data.intervalMs;
				console.log(`🚀 [KEEPALIVE_SERVICE] Keepalive started (${this.intervalMs}ms interval)`);
				break;
			case "KEEPALIVE_STOPPED":
				this.isActive = false;
				console.log("🛑 [KEEPALIVE_SERVICE] Keepalive stopped");
				break;
			case "KEEPALIVE_TICK":
				if (this.onKeepaliveTick) {
					this.onKeepaliveTick();
				}
				console.log(`💓 [KEEPALIVE_SERVICE] Keepalive tick received at ${new Date(timestamp).toISOString()}`);
				break;
			case "INTERVAL_UPDATED":
				this.intervalMs = event.data.intervalMs;
				console.log(`🔄 [KEEPALIVE_SERVICE] Interval updated to ${this.intervalMs}ms`);
				break;
			case "PONG":
				console.log(`🏓 [KEEPALIVE_SERVICE] Worker pong received (active: ${event.data.workerActive})`);
				break;
			case "WORKER_ERROR":
				console.error("❌ [KEEPALIVE_SERVICE] Worker error:", event.data.error);
				if (this.onWorkerError) {
					this.onWorkerError(event.data.error);
				}
				break;
		}
	}

	private handleWorkerError(error: any): void {
		console.error("❌ [KEEPALIVE_SERVICE] Web Worker error:", error);
		if (this.onWorkerError) {
			this.onWorkerError(error);
		}
	}

	private sendCommand(command: KeepaliveWorkerCommand): void {
		if (!this.worker) {
			console.error("❌ [KEEPALIVE_SERVICE] Cannot send command - worker not initialized");
			return;
		}
		try {
			this.worker.postMessage(command);
			console.log(`📤 [KEEPALIVE_SERVICE] Sent command: ${command.type}`, command);
		} catch (error) {
			console.error(`❌ [KEEPALIVE_SERVICE] Failed to send command ${command.type}:`, error);
		}
	}

	public startKeepalive(intervalMs: number = this.intervalMs): void {
		console.log(`🚀 [KEEPALIVE_SERVICE] Starting keepalive with ${intervalMs}ms interval`);
		this.sendCommand({ type: "START_KEEPALIVE", data: { intervalMs } });
	}

	public stopKeepalive(): void {
		console.log("🛑 [KEEPALIVE_SERVICE] Stopping keepalive");
		this.sendCommand({ type: "STOP_KEEPALIVE" });
	}

	public updateInterval(intervalMs: number): void {
		console.log(`🔄 [KEEPALIVE_SERVICE] Updating interval to ${intervalMs}ms`);
		this.sendCommand({ type: "UPDATE_INTERVAL", data: { intervalMs } });
	}

	public pingWorker(): void {
		console.log("🏓 [KEEPALIVE_SERVICE] Pinging Web Worker");
		this.sendCommand({ type: "PING" });
	}

	public setOnKeepaliveTick(callback: () => void): void {
		this.onKeepaliveTick = callback;
		console.log("📋 [KEEPALIVE_SERVICE] Keepalive tick callback registered");
	}

	public setOnWorkerError(callback: (error: any) => void): void {
		this.onWorkerError = callback;
		console.log("📋 [KEEPALIVE_SERVICE] Worker error callback registered");
	}

	public getStatus() {
		return {
			isActive: this.isActive,
			intervalMs: this.intervalMs,
			workerAvailable: !!this.worker
		};
	}

	public terminate(): void {
		if (this.worker) {
			console.log("🔄 [KEEPALIVE_SERVICE] Terminating Web Worker");
			this.worker.terminate();
			this.worker = null;
		}
		this.isActive = false;
		this.onKeepaliveTick = undefined;
		this.onWorkerError = undefined;
	}
}

export const keepaliveWorkerService = new KeepaliveWorkerService();